import { CommonModule, NgFor } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccountService } from '../../../../Services/Accounts/account.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { NotificationService } from '../../../../Services/notification.service';
import { AuthService } from '../../../../Services/auth.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-update-all-accounts',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './update-all-accounts.component.html',
  styleUrl: './update-all-accounts.component.css'
})
export class UpdateAllAccountsComponent implements OnInit, OnDestroy {
  rows: any[] = [];
  accountTypeMappings: any = {};
  accounts: string[] = ['Advance Money', 'Bank Account', 'Bank OD Acc', 'Branch/Divisions', 'Capital Account', 'Card Payment', 'Cash in Hand', 'CGST', 'Courier Charges', 'Current Assets', 'Current Liabilities', 'Deposits (Asset)', 'Direct Expenses', 'Direct Incomes', 'Discount Paid', 'Discount Rec', 'Duties & Taxes', 'Employee & Staff', 'Fixed Assets', 'GST Payable', 'Hallmarking Chrg', 'IGST', 'Indirect Expenses', 'Indirect Incomes', 'Interest Paid', 'Interest Rec', 'Investments', 'Labour Charges', 'Loans', 'Loans & Advances', 'Making Charges', 'Metal Rate Cut Pnl', 'Misc Expenses', 'MLJ', 'Online Payment', 'Other Charges', 'Paytm POS', 'Phone Pe', 'Processing Amount', 'Profit & Loss Acc', 'Provisions', 'Purchase Accounts', 'Purchase Gold', 'Purchase RAW Gold', 'Purchase RAW Metal', 'Purchase RAW Silver', 'Purchase Silver', 'Purchase Stone', 'RAW Alloy', 'RAW Gold', 'RAW Metal', 'RAW Silver', 'Razor Pay', 'Reserves & Surplus', 'Round Off', 'Sales Accounts', 'Sales crystal', 'Sales Gold', 'Sales RAW Gold', 'Sales RAW Metal', 'Sales RAW Silver', 'Sales Silver', 'Sales Stone', 'Saving Scheme', 'Secured Loans', 'SGST', 'Stock (Inventory)', 'Stock crystal', 'Stock Gold', 'Stock in Hand', 'Stock Raw Gold', 'Stock Raw Silver', 'Stock Silver', 'Stock Stone', 'Sundry Creditors', 'Sundry Debtors', 'Suspense Account', 'TCS', 'TDS', 'Unsecured Loans'];
  crdrTypes: string[] = ['CR', 'DR'];

  totalCr: number = 0;
  totalDr: number = 0;
  difference: number = 0;
  searchTerm: string = '';

  private firmSubscription: Subscription | null = null;
  currentFirmId: number | null = null;

  constructor(
    private accountService: AccountService,
    private firmSelectionService: FirmSelectionService,
    private notificationService: NotificationService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.getAccountDropdowns();
    this.firmSubscription = this.firmSelectionService.selectedFirmName$.subscribe(firm => {
      this.currentFirmId = firm ? firm.id : null;
      if (this.currentFirmId) {
        this.loadAccounts();
      } else {
        this.rows = [];
        this.addRow();
      }
    });
  }

  loadAccounts() {
    if (!this.currentFirmId) return;

    const params: any = { firm_id: this.currentFirmId };

    // If staff is logged in, only show accounts created by this staff
    const userRole = this.authService.getUserRole();
    if (userRole === 'Staff') {
      params.created_by = this.authService.getUserId();
    }

    this.accountService.getAccounts(params).subscribe({
      next: (res: any) => {
        const accounts = res.data || [];
        if (accounts.length > 0) {
          this.rows = accounts.map((acc: any) => ({
            id: acc.acc_id || acc.id,
            acc_user_acc: acc.acc_user_acc,
            acc_main_acc: acc.acc_main_acc,
            acc_cash_balance: acc.acc_cash_balance,
            acc_cash_balance_crdr: acc.acc_cash_balance_crdr || 'CR'
          }));
        } else {
          this.rows = [];
          this.addRow();
        }
        this.calculateTotals();
      },
      error: (err) => {
        console.error('Error loading accounts:', err);
        this.rows = [];
        this.addRow();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.firmSubscription) {
      this.firmSubscription.unsubscribe();
    }
  }

  getAccountDropdowns() {
    this.accountService.getDropdowns().subscribe({
      next: (res: any) => {
        const dynamicTypes = res.data?.main_accounts || [];
        this.accountTypeMappings = res.data?.mappings || {};
        const staticAccountTypes = this.accounts;
        this.accounts = Array.from(new Set([...staticAccountTypes, ...dynamicTypes])).sort();
      }
    });
  }

  get filteredRows() {
    if (!this.searchTerm) return this.rows;
    const search = this.searchTerm.toLowerCase();
    return this.rows.filter(row =>
      (row.acc_user_acc || '').toLowerCase().includes(search) ||
      (row.acc_main_acc || '').toLowerCase().includes(search)
    );
  }

  addRow() {
    this.rows.push({
      acc_user_acc: '',
      acc_main_acc: 'Advance Money',
      acc_cash_balance: null,
      acc_cash_balance_crdr: 'CR'
    });
    this.calculateTotals();
  }

  removeRow(row: any) {
    const index = this.rows.indexOf(row);
    if (index === -1) return;

    if (this.rows.length > 1) {
      this.rows.splice(index, 1);
      this.calculateTotals();
    } else {
      this.rows[0] = {
        acc_user_acc: '',
        acc_main_acc: 'Advance Money',
        acc_cash_balance: null,
        acc_cash_balance_crdr: 'CR'
      };
      this.calculateTotals();
    }
  }

  calculateTotals() {
    this.totalCr = 0;
    this.totalDr = 0;
    this.rows.forEach(row => {
      const amount = Number(row.acc_cash_balance) || 0;
      if (row.acc_cash_balance_crdr === 'CR') {
        this.totalCr += amount;
      } else {
        this.totalDr += amount;
      }
    });
    this.difference = Math.abs(this.totalCr - this.totalDr);
  }

  mapAccountCategory(type: string): { primary: string, group: string } {
    if (this.accountTypeMappings[type]) {
      return {
        primary: this.accountTypeMappings[type].primary,
        group: this.accountTypeMappings[type].group
      };
    }
    return { primary: 'Assets', group: 'Current Assets' };
  }

  onSubmit() {
    if (!this.currentFirmId) {
      this.notificationService.showError('Please select a firm first', 'Error');
      return;
    }

    const payload = this.rows
      .filter(row => row.acc_user_acc.trim() !== '')
      .map(row => {
        const mapping = this.mapAccountCategory(row.acc_main_acc);
        return {
          ...row,
          acc_firm_id: this.currentFirmId,
          acc_pri_acc: mapping.primary,
          acc_pri_grp: mapping.group,
          acc_created_by_user: this.authService.getUserId(),
          acc_cash_balance_date: new Date().toISOString().split('T')[0]
        };
      });

    if (payload.length === 0) {
      this.notificationService.showError('Please enter at least one account name', 'Error');
      return;
    }

    this.accountService.bulkCreate(payload).subscribe({
      next: (res: any) => {
        this.notificationService.showSuccess('Accounts updated successfully', 'Success');
        this.loadAccounts();
      },
      error: (err: any) => {
        console.error('Error in bulk update:', err);
        this.notificationService.showError(err.error?.message || 'Failed to update accounts', 'Error');
      }
    });
  }
}
